import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Put } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from './pipelineUtils';
import { Gated } from '@/auth/permissions';

const EMPTY_EMH = { easy: 0, medium: 0, hard: 0 };

/** Legacy count-only quota → seed medium so Save upgrades to E/M/H. */
function hydrateEmh(raw) {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_EMH };
  if ('count' in raw && !('easy' in raw || 'medium' in raw || 'hard' in raw)) {
    return { easy: 0, medium: Number(raw.count || 0), hard: 0 };
  }
  return {
    easy: Number(raw.easy || 0),
    medium: Number(raw.medium || 0),
    hard: Number(raw.hard || 0),
  };
}

/**
 * Pipeline question quotas — HireIQ draws a fresh random set per evaluation.
 * Classic ordered bindings are removed; configure E/M/H (Offer stays count-only).
 */
export function PartnerQuestionQuotas({ pipelineId, pipelineLabel, stages = [] }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['pipeline-partner-quotas', pipelineId],
    queryFn: () => v1Get(`/pipelines/${pipelineId}/partner-question-quotas`),
  });
  const [draft, setDraft] = useState(null);

  const panelRounds = React.useMemo(() => {
    const fromStages = (stages || [])
      .filter((s) => (s.code === 'PANEL' || s.code === 'CODE_PANEL') && s.id)
      .map((s) => ({
        key: `PANEL:${s.id}`,
        label: s.label || (s.code === 'CODE_PANEL' ? 'Code Panel' : 'Technical Panel'),
        stageId: s.id,
        code: s.code,
      }));
    if (fromStages.length) return fromStages;
    // Fall back to keys returned by the quotas API (saved current version).
    const avail = data?.available || {};
    return Object.keys(avail)
      .filter((k) => k.startsWith('PANEL:'))
      .map((k) => ({
        key: k,
        label: avail[k]?.label || 'Technical Panel',
        stageId: avail[k]?.stage_id || k.split(':')[1],
        code: (avail[k]?.stage_code || 'panel').toUpperCase(),
      }));
  }, [stages, data]);

  const hasVb = stages.some((s) => s.code === 'VIDEOBOT');
  const hasMcq = stages.some((s) => s.code === 'MCQ');
  const hasTech = stages.some((s) => s.code === 'TECH_ASSESSMENT');
  const hasOffer = stages.some((s) => s.code === 'OFFER');

  React.useEffect(() => {
    if (!data) return;
    const q = data.quotas || {};
    const next = {
      VIDEOBOT: hydrateEmh(q.VIDEOBOT),
      MCQ: hydrateEmh(q.MCQ),
      TECH_ASSESSMENT: hydrateEmh(q.TECH_ASSESSMENT),
      OFFER: { count: Number(q.OFFER?.count || 0) },
      panels: {},
    };
    for (const round of panelRounds) {
      next.panels[round.key] = hydrateEmh(q[round.key]);
    }
    setDraft(next);
  }, [data, pipelineId, panelRounds]);

  if (!data || !draft) {
    return (
      <div className="mt-5 rounded-xl border bg-secondary/40 p-4 text-xs text-muted-foreground">
        Loading question quotas…
      </div>
    );
  }

  const available = data.available || {};
  const configStatus = data.config_status || 'draft';
  const clamp = (stage, key, value) => {
    const max = Number(available?.[stage]?.[key] ?? available?.[stage]?.count ?? 0);
    const n = Math.max(0, Number(value) || 0);
    return max > 0 ? Math.min(n, max) : n;
  };

  const clampEmh = (stage, emh) => ({
    easy: clamp(stage, 'easy', emh.easy),
    medium: clamp(stage, 'medium', emh.medium),
    hard: clamp(stage, 'hard', emh.hard),
  });

  const save = async () => {
    const quotas = {};
    if (hasVb) {
      const vb = clampEmh('VIDEOBOT', draft.VIDEOBOT);
      if (vb.easy + vb.medium + vb.hard > 0) quotas.VIDEOBOT = vb;
    }
    if (hasMcq) {
      const mcq = clampEmh('MCQ', draft.MCQ);
      if (mcq.easy + mcq.medium + mcq.hard > 0) quotas.MCQ = mcq;
    }
    if (hasTech) {
      const tech = clampEmh('TECH_ASSESSMENT', draft.TECH_ASSESSMENT);
      if (tech.easy + tech.medium + tech.hard > 0) quotas.TECH_ASSESSMENT = tech;
    }
    if (hasOffer) {
      const n = clamp('OFFER', 'count', draft.OFFER.count);
      if (n > 0) quotas.OFFER = { count: n };
    }
    for (const round of panelRounds) {
      const panel = clampEmh(round.key, draft.panels[round.key] || EMPTY_EMH);
      if (panel.easy + panel.medium + panel.hard > 0) quotas[round.key] = panel;
    }
    if (!Object.keys(quotas).length) {
      toast.error('Set quotas greater than zero for every assessment stage on this pipeline');
      return;
    }
    try {
      const res = await v1Put(`/pipelines/${pipelineId}/partner-question-quotas`, { quotas });
      qc.invalidateQueries({ queryKey: ['pipeline-partner-quotas', pipelineId] });
      qc.invalidateQueries({ queryKey: ['v1-pipelines'] });
      qc.invalidateQueries({ queryKey: ['v1-pipeline', pipelineId] });
      const status = res?.config_status || 'draft';
      toast.success(
        status === 'ready'
          ? 'Question quotas saved — pipeline is Ready'
          : 'Quotas saved — pipeline still Draft (cover every stage)',
      );
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, 'Save failed — check bank depth'));
    }
  };

  const Num = ({ label, stage, field, maxKey, value, onChange }) => {
    const max = Number(available?.[stage]?.[maxKey || field] ?? available?.[stage]?.count ?? 0);
    const val = value;
    return (
      <label className="flex flex-col gap-1 text-[11px]">
        <span className="font-medium text-muted-foreground">
          {label} <span className="font-normal">(max {max})</span>
        </span>
        <input
          type="number"
          min={0}
          max={max || undefined}
          value={val}
          disabled={max < 1 && val < 1}
          onChange={(e) => onChange(clamp(stage, maxKey || field, e.target.value))}
          className="h-8 w-20 rounded-md border bg-background px-2 text-xs"
          data-testid={`partner-quota-${stage}-${field}`}
        />
      </label>
    );
  };

  const EmhFields = ({ stage, value, onChange }) => (
    <div className="mt-2 flex flex-wrap gap-3">
      <Num label="Easy" stage={stage} field="easy" value={value.easy} onChange={(n) => onChange({ ...value, easy: n })} />
      <Num label="Medium" stage={stage} field="medium" value={value.medium} onChange={(n) => onChange({ ...value, medium: n })} />
      <Num label="Hard" stage={stage} field="hard" value={value.hard} onChange={(n) => onChange({ ...value, hard: n })} />
    </div>
  );

  const readyBadge =
    configStatus === 'ready' ? (
      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-800" data-testid="pipeline-config-status">
        Ready
      </span>
    ) : (
      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-900" data-testid="pipeline-config-status">
        Draft
      </span>
    );

  return (
    <div className="mt-5 rounded-xl border border-amber-200/80 bg-amber-50/40 p-4" data-testid="pipeline-partner-quotas">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-900">
              Question quotas — {pipelineLabel}
            </p>
            {readyBadge}
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            HireIQ draws a fresh random set per evaluation at stage start from the role bank
            (IQ Assessment is global). Set Easy / Medium / Hard per stage; HR / Offer stays a
            plain count. Pipeline stays Draft until quotas cover every assessment stage.
          </p>
        </div>
        <Gated need="pipeline.edit">
          <Button size="sm" onClick={save} className="gap-1.5" data-testid="pipeline-partner-quotas-save">
            <Save className="h-3.5 w-3.5" /> Save quotas
          </Button>
        </Gated>
      </div>
      <div className="mt-3 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {hasVb && (
          <div className="rounded-lg border bg-card p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Video Bot</p>
            <EmhFields
              stage="VIDEOBOT"
              value={draft.VIDEOBOT}
              onChange={(next) => setDraft({ ...draft, VIDEOBOT: next })}
            />
          </div>
        )}
        {hasMcq && (
          <div className="rounded-lg border bg-card p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">IQ Assessment</p>
            <EmhFields
              stage="MCQ"
              value={draft.MCQ}
              onChange={(next) => setDraft({ ...draft, MCQ: next })}
            />
          </div>
        )}
        {hasTech && (
          <div className="rounded-lg border bg-card p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Tech Assessment</p>
            <EmhFields
              stage="TECH_ASSESSMENT"
              value={draft.TECH_ASSESSMENT}
              onChange={(next) => setDraft({ ...draft, TECH_ASSESSMENT: next })}
            />
          </div>
        )}
        {panelRounds.map((round) => (
          <div key={round.key} className="rounded-lg border bg-card p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{round.label}</p>
            <EmhFields
              stage={round.key}
              value={draft.panels[round.key] || EMPTY_EMH}
              onChange={(next) => setDraft({
                ...draft,
                panels: { ...draft.panels, [round.key]: next },
              })}
            />
          </div>
        ))}
        {hasOffer && (
          <div className="rounded-lg border bg-card p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Offer / HR</p>
            <div className="mt-2">
              <Num
                label="Count"
                stage="OFFER"
                field="count"
                value={draft.OFFER.count}
                onChange={(n) => setDraft({ ...draft, OFFER: { count: n } })}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Classic binders removed — kept as no-op exports so any stale imports fail softly in tests.
export function VideoBotBinding() {
  return null;
}
export function McqBinding() {
  return null;
}
export function TechAssessmentBinding() {
  return null;
}
